import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Tooltip,
  Button,
  Input,
  Form,
  Select,
  Row,
  Col,
  Card,
  Tabs,
  Tag,
  Space,
  Checkbox,
  InputNumber,
  DatePicker,
  Divider,
  Descriptions,
  Popconfirm } from 'antd';
import { PlusOutlined, EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';

import {
  save_cleaning_type_for_line,
  update_cleaning_type,
  get_cleaning_type_for_line,
  delete_cleaning_type,
} from '../../../utils/api';

import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import StatCard from '@/components/StatCard';
import { Sparkles, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import { Typography } from 'antd';
import { formatDate } from '@/utils/datetime';
import DataTable from '@/components/DataTable';
import SopLinks from '@/components/SopLinks';
import PageHeader, { EmptyState } from '@/components/PageHeader';
import PageModal from '@/components/PageModal';
import { PageInfo } from '@/components/PageChrome';

const { Option } = Select;
// Backend routes update_cleaning_type / delete_cleaning_type are commented out in
// djangoapp/urls.py ("disabled per request 2026-07-09"); list + add still work.
const CLEANING_EDIT_ENABLED = false;
const { TextArea } = Input;

// ✅ Color-coded status maps
const riskLevelColors = {
  LOW: 'green',
  MEDIUM: 'orange',
  HIGH: 'red',
  CRITICAL: 'purple',
};

const validationStatusColors = {
  NOT_VALIDATED: 'default',
  IN_PROGRESS: 'processing',
  VALIDATED: 'success',
  REVALIDATION_DUE: 'warning',
};

// Cleaning class is the tier the trigger resolver and the execution gate read; the GMP
// category stays the finer classification beneath it.
const cleaningClassColors = {
  TYPE_A: 'blue',
  TYPE_B: 'volcano',
  TYPE_C: 'purple',
};

const cleaningClassShort = {
  TYPE_A: 'Type A',
  TYPE_B: 'Type B',
  TYPE_C: 'Type C',
};

const cleaningClassHint = {
  TYPE_A: 'Same-product campaign continuation — minor clean, visual verification only.',
  TYPE_B: 'Changeover / campaign end / post-maintenance / post-contamination — full clean with swab and rinse testing and QA release.',
  TYPE_C: 'Sanitisation only.',
};

// The requirements a downstream gate acts on, read straight off the flags rather than prose.
const verificationTags = (record) => {
  const tags = [];
  if (record.visual_inspection_required) tags.push({ label: 'Visual', color: 'default' });
  if (record.swab_test_required) tags.push({ label: 'Swab', color: 'red' });
  if (record.rinse_test_required) tags.push({ label: 'Rinse', color: 'orange' });
  if (record.microbial_test_required) tags.push({ label: 'Microbial', color: 'magenta' });
  return tags;
};

// Flatten verification_limits ({swab:[…], rinse:[…]}) into readable "label NMT value uom" lines.
const limitLines = (limits) => {
  if (!limits || typeof limits !== 'object') return [];
  const out = [];
  ['swab', 'rinse'].forEach((group) => {
    (limits[group] || []).forEach((l) => {
      if (l.min !== undefined && l.min !== null && l.max !== undefined && l.max !== null) {
        out.push(`${l.label}: ${l.min}–${l.max} ${l.uom || ''}`.trim());
      } else if (l.max !== undefined && l.max !== null) {
        out.push(`${l.label}: NMT ${l.max} ${l.uom || ''}`.trim());
      }
    });
  });
  return out;
};

const CleaningTypes = ({ show_config }) => {
  const { isDark } = useTheme();
  const [showForm, setShowForm] = useState(false);
  const [CleaningData, setCleaningData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const { t } = useTranslation();

  // ✅ API success handlers
  const successCleaningList = (res) => {
    if (res?.data?.status === 'failed') {
      toast.warning(res?.data?.message);
      return;
    }
    setCleaningData(res?.data?.data || []);
  };

  // ✅ Fetch data
  const getAllData = async () => {
    try {
      await get_cleaning_type_for_line(successCleaningList);
    } catch (e) {
      console.error('Error fetching data:', e);
      toast.error(e?.response?.data?.message || 'Failed to load cleaning types');
    }
  };

  useEffect(() => {
    getAllData();
  }, []);

  useEffect(() => {
    setShowForm(false);
  }, [show_config]);

  // ✅ Add or update Cleaning
  const handleAdd = async (values, resetForm) => {
    try {
      if (isEdit) {
        // Use UPDATE API when editing
        await update_cleaning_type(values, handleSuccess(resetForm));
      } else {
        // Use ADD API when creating
        await save_cleaning_type_for_line(values, handleSuccess(resetForm));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSuccess = (resetForm) => (res) => {
    if (res.status === 'failed') {
      toast.warning(res.message);
    } else {
      toast.success(res.message);
      getAllData();
      setShowForm(false);
      setIsEdit(false);
      setEditData(null);
      if (resetForm) resetForm();
    }
  };

  // ✅ Handle view details
  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  // ✅ Handle edit
  const handleEdit = (record) => {
    setIsEdit(true);
    setEditData(record);
    setShowForm(true);
  };

  // ✅ Handle delete
  const handleDelete = async (record) => {
    try {
      await delete_cleaning_type({ id: record?.id }, (res) => {
        if (res?.status === 'success') {
          toast.success(res?.message || 'Cleaning type deleted successfully');
          getAllData();
        } else {
          toast.warning(res?.message || 'Failed to delete cleaning type');
        }
      });
    } catch (error) {
      console.error('Error deleting cleaning type:', error);
      toast.error('Failed to delete cleaning type');
    }
  };

  // Enhanced table columns with useTableConfig properties
  const allColumns = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      defaultVisible: false,
      sortType: 'number',
      width: 80,
      align: 'center',
    },
    {
      key: 'cleaning_code',
      title: 'Code',
      dataIndex: 'cleaning_code',
      defaultVisible: true,
      sortType: 'string',
      width: 100,
      align: 'center',
      render: (text) => text || '-',
    },
    {
      key: 'name',
      title: 'Cleaning Type',
      dataIndex: 'name',
      defaultVisible: true,
      sortType: 'string',
      width: 200,
      align: 'left',
    },
    {
      key: 'cleaning_class',
      title: 'Class',
      dataIndex: 'cleaning_class',
      defaultVisible: true,
      sortType: 'string',
      width: 110,
      align: 'center',
      render: (cls) =>
        cls ? (
          <Tooltip title={cleaningClassHint[cls]}>
            <Tag color={cleaningClassColors[cls]}>{cleaningClassShort[cls] || cls}</Tag>
          </Tooltip>
        ) : (
          <Tooltip title="Unclassified — the trigger resolver cannot select this type until a class is set.">
            <Tag>Unclassified</Tag>
          </Tooltip>
        ),
    },
    {
      key: 'cleaning_category',
      title: 'Category',
      dataIndex: 'cleaning_category_display',
      defaultVisible: true,
      sortType: 'string',
      width: 150,
      align: 'center',
      render: (text) => text || '-',
    },
    {
      key: 'cleaning_method',
      title: 'Method',
      dataIndex: 'cleaning_method_display',
      defaultVisible: true,
      sortType: 'string',
      width: 150,
      align: 'center',
      render: (text) => text || '-',
    },
    {
      key: 'duration',
      title: 'Duration',
      dataIndex: 'estimated_duration_minutes',
      defaultVisible: true,
      sortType: 'number',
      width: 100,
      align: 'center',
      render: (mins) => (mins ? `${mins} min` : '-'),
    },
    {
      key: 'verification',
      title: 'Verification',
      dataIndex: 'verification_method',
      defaultVisible: true,
      width: 190,
      align: 'center',
      render: (_, record) => {
        const tags = verificationTags(record);
        const lines = limitLines(record.verification_limits);
        if (!tags.length) return '-';
        const body = (
          <Space size={4} wrap>
            {tags.map((tg) => (
              <Tag key={tg.label} color={tg.color} style={{ fontSize: '11px', marginInlineEnd: 0 }}>
                {tg.label}
              </Tag>
            ))}
          </Space>
        );
        return lines.length ? (
          <Tooltip title={<span>{lines.map((l) => (<span key={l}>{l}<br /></span>))}</span>}>{body}</Tooltip>
        ) : (
          body
        );
      },
    },
    {
      key: 'campaign',
      title: 'Campaign Limit',
      dataIndex: 'campaign_batch_limit',
      defaultVisible: true,
      sortType: 'number',
      width: 150,
      align: 'center',
      render: (_, record) => {
        const parts = [];
        if (record.campaign_batch_limit) parts.push(`${record.campaign_batch_limit} batches`);
        if (record.campaign_max_days) parts.push(`${record.campaign_max_days} days`);
        return parts.length ? (
          <Tooltip title="How long one clean of this type may carry a same-product campaign before a full clean is due.">
            <span>{parts.join(' / ')}</span>
          </Tooltip>
        ) : (
          '-'
        );
      },
    },
    {
      key: 'holds',
      title: 'Holds (dirty / clean)',
      dataIndex: 'hold_time_hours',
      defaultVisible: true,
      sortType: 'number',
      width: 160,
      align: 'center',
      render: (_, record) => {
        const dirty = record.dirty_hold_hours ? `${record.dirty_hold_hours} h` : '-';
        const clean = record.hold_time_hours ? `${record.hold_time_hours} h` : '-';
        return (
          <Tooltip title="Dirty hold: end of last use to start of cleaning. Clean hold: approval to expiry of the clean.">
            <span>{`${dirty} / ${clean}`}</span>
          </Tooltip>
        );
      },
    },
    {
      key: 'risk_level',
      title: 'Risk Level',
      dataIndex: 'risk_level',
      defaultVisible: true,
      sortType: 'string',
      width: 120,
      align: 'center',
      render: (risk, record) =>
        risk ? <Tag color={riskLevelColors[risk]}>{record.risk_level_display}</Tag> : '-',
    },
    {
      key: 'validation_status',
      title: 'Validation Status',
      dataIndex: 'validation_status',
      defaultVisible: true,
      sortType: 'string',
      width: 150,
      align: 'center',
      render: (status, record) =>
        status ? (
          <Tag color={validationStatusColors[status]}>{record.validation_status_display}</Tag>
        ) : (
          '-'
        ),
    },
    {
      key: 'approvals',
      title: 'Approvals Required',
      dataIndex: 'approvals',
      defaultVisible: false,
      width: 150,
      align: 'center',
      render: (_, record) => {
        const approvals = [];
        if (record.requires_supervisor_approval) approvals.push('Supervisor');
        if (record.requires_qa_approval) approvals.push('QA');
        return approvals.length > 0 ? (
          <Space size={4} wrap>
            {approvals.map((a) => (
              <Tag key={a} style={{ fontSize: '11px' }}>
                {a}
              </Tag>
            ))}
          </Space>
        ) : (
          '-'
        );
      },
    },
    {
      key: 'is_active',
      title: 'Status',
      dataIndex: 'is_active',
      defaultVisible: true,
      sortType: 'string',
      width: 100,
      align: 'center',
      render: (active) => (
        <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      key: 'created_at',
      title: 'Created Date',
      dataIndex: 'created_at',
      defaultVisible: false,
      sortType: 'date',
      width: 150,
      align: 'center',
      render: (date) => (date ? formatDate(date) : '-'),
    },
    {
      key: 'updated_at',
      title: 'Last Updated',
      dataIndex: 'updated_at',
      defaultVisible: false,
      sortType: 'date',
      width: 150,
      align: 'center',
      render: (date) => (date ? formatDate(date) : '-'),
    },
    {
      key: 'actions',
      title: 'Actions',
      dataIndex: 'actions',
      defaultVisible: true,
      alwaysVisible: true,
      width: CLEANING_EDIT_ENABLED ? 260 : 110,
      align: 'center',
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          {/* Edit / Delete hidden: update_cleaning_type & delete_cleaning_type routes are
              disabled on the backend ("disabled per request 2026-07-09"). */}
          {CLEANING_EDIT_ENABLED && (
            <Button
              size="small"
              aria-label={`Edit ${record?.name}`}
              icon={<Icons.Pencil className="w-3.5 h-3.5 text-amber-600" />}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
          <Button
            size="small"
            aria-label={`View details of ${record?.name}`}
            icon={<Icons.Eye className="w-3.5 h-3.5" style={{ color: defaultColors.primary }} />}
            onClick={() => handleViewDetails(record)}
          >
            View
          </Button>
          {CLEANING_EDIT_ENABLED && (
            <Popconfirm
              title={`Are you sure you want to delete "${record?.name}"?`}
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" danger aria-label={`Delete ${record?.name}`} icon={<Icons.Trash2 className="w-3.5 h-3.5" />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  // ✅ Validation schema (basic fields only for now)
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Cleaning name is required'),
    description: Yup.string().nullable(),
    cleaning_code: Yup.string().nullable(),
    cleaning_class: Yup.string().nullable(),
    campaign_batch_limit: Yup.number().nullable().min(1, 'Must be at least 1 batch'),
    campaign_max_days: Yup.number().nullable().min(0, 'Cannot be negative'),
    dirty_hold_hours: Yup.number().nullable().min(0, 'Cannot be negative'),
    cleaning_category: Yup.string().nullable(),
    cleaning_method: Yup.string().nullable(),
    estimated_duration_minutes: Yup.number().nullable().min(0),
    risk_level: Yup.string().nullable(),
  });

  // ✅ Render detail drawer with comprehensive tabs
  const renderDetailDrawer = () => {
    if (!selectedRecord) return null;

    const items = [
      {
        key: '1',
        label: 'General Information',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Cleaning Code" span={2}>
              {selectedRecord.cleaning_code || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Name" span={2}>
              {selectedRecord.name}
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {selectedRecord.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Class">
              {selectedRecord.cleaning_class ? (
                <Tag color={cleaningClassColors[selectedRecord.cleaning_class]}>
                  {selectedRecord.cleaning_class_display || selectedRecord.cleaning_class}
                </Tag>
              ) : (
                <Tag>Unclassified</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Category">
              {selectedRecord.cleaning_category_display || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Method">
              {selectedRecord.cleaning_method_display || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedRecord.is_active ? 'green' : 'default'}>
                {selectedRecord.is_active ? 'Active' : 'Inactive'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="cGMP Critical">
              {selectedRecord.cgmp_critical ? 'Yes' : 'No'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '2',
        label: 'Frequency & Timing',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Frequency Requirement" span={2}>
              {selectedRecord.frequency_requirement || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Estimated Duration">
              {selectedRecord.estimated_duration_minutes
                ? `${selectedRecord.estimated_duration_minutes} minutes`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Drying Time">
              {selectedRecord.drying_time_minutes
                ? `${selectedRecord.drying_time_minutes} minutes`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Clean Hold">
              {selectedRecord.hold_time_hours ? `${selectedRecord.hold_time_hours} hours` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Dirty Hold">
              {selectedRecord.dirty_hold_hours ? `NMT ${selectedRecord.dirty_hold_hours} hours` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Campaign Limit" span={2}>
              {selectedRecord.campaign_batch_limit || selectedRecord.campaign_max_days
                ? [
                    selectedRecord.campaign_batch_limit
                      ? `${selectedRecord.campaign_batch_limit} batches`
                      : null,
                    selectedRecord.campaign_max_days ? `${selectedRecord.campaign_max_days} days` : null,
                  ]
                    .filter(Boolean)
                    .join(' / ')
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '3',
        label: 'Materials & Agents',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Cleaning Agents" span={2}>
              {selectedRecord.cleaning_agents || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Detergent Type">
              {selectedRecord.detergent_type || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Sanitizer Type">
              {selectedRecord.sanitizer_type || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Rinse Agent">
              {selectedRecord.rinse_agent || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Water Quality">
              {selectedRecord.water_quality_display || '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '4',
        label: 'Equipment & Personnel',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Equipment/Tools Required" span={2}>
              {selectedRecord.equipment_tools_required || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="PPE Requirements" span={2}>
              {selectedRecord.ppe_requirements || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Minimum Personnel">
              {selectedRecord.minimum_personnel_required || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Qualification Required">
              {selectedRecord.operator_qualification_required || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Training Required">
              {selectedRecord.training_required ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Training Reference">
              {selectedRecord.training_reference || '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '5',
        label: 'Verification & Testing',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Verification Method" span={2}>
              {selectedRecord.verification_method_display || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Acceptance Criteria" span={2}>
              {selectedRecord.acceptance_criteria || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Visual Inspection">
              {selectedRecord.visual_inspection_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Swab Test">
              {selectedRecord.swab_test_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Rinse Test">
              {selectedRecord.rinse_test_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Microbial Test">
              {selectedRecord.microbial_test_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Residue Limit" span={2}>
              {selectedRecord.residue_limit_ppm ? `${selectedRecord.residue_limit_ppm} ppm` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Acceptance Limits" span={2}>
              {limitLines(selectedRecord.verification_limits).length ? (
                <Space direction="vertical" size={2}>
                  {limitLines(selectedRecord.verification_limits).map((l) => (
                    <span key={l}>{l}</span>
                  ))}
                </Space>
              ) : (
                'None typed — verification is visual only'
              )}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '6',
        label: 'Documentation',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="SOP Reference">
              {selectedRecord.sop_reference || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Checklist Required">
              {selectedRecord.checklist_required ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Photographic Evidence">
              {selectedRecord.photographic_evidence_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Batch Record Entry">
              {selectedRecord.batch_record_entry_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '7',
        label: 'Validation & Compliance',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Validation Required">
              {selectedRecord.validation_required ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Validation Status">
              <Tag color={validationStatusColors[selectedRecord.validation_status]}>
                {selectedRecord.validation_status_display || '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Protocol Reference" span={2}>
              {selectedRecord.validation_protocol_reference || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Last Validation Date">
              {selectedRecord.last_validation_date || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Next Validation Date">
              {selectedRecord.next_validation_date || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="cGMP Critical">
              {selectedRecord.cgmp_critical ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Regulatory Requirement" span={2}>
              {selectedRecord.regulatory_requirement || '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '8',
        label: 'Risk & Safety',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Risk Level" span={2}>
              <Tag color={riskLevelColors[selectedRecord.risk_level]}>
                {selectedRecord.risk_level_display || '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Safety Precautions" span={2}>
              {selectedRecord.safety_precautions || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Environmental Controls" span={2}>
              {selectedRecord.environmental_controls_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '9',
        label: 'Applicability',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Applicable Equipment" span={2}>
              {selectedRecord.applicable_equipment || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Applicable Areas" span={2}>
              {selectedRecord.applicable_areas || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Product Contact Surfaces Only" span={2}>
              {selectedRecord.product_contact_surfaces_only ? 'Yes' : 'No'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '10',
        label: 'Approvals & Quality',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Supervisor Approval">
              {selectedRecord.requires_supervisor_approval ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="QA Approval">
              {selectedRecord.requires_qa_approval ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Critical to Quality">
              {selectedRecord.critical_to_quality ? 'Yes' : 'No'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: 'sops',
        label: 'SoPs',
        children: (
          <SopLinks objectType="CLEANING_TYPE" objectRef={selectedRecord.id} title="Cleaning procedures" />
        ),
      },
      {
        key: '11',
        label: 'Audit Trail',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Version Number">
              {selectedRecord.version_number || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created By">
              {selectedRecord.created_by || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created On">
              {selectedRecord.created_on || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Modified By">
              {selectedRecord.modified_by || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Modified On" span={2}>
              {selectedRecord.modified_on || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Notes" span={2}>
              {selectedRecord.notes || '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
    ];

    return (
      <PageModal
        open={drawerVisible}
        icon={<Icons.Eye size={22} />}
        title="Cleaning Type Details"
        description={selectedRecord.name}
        width={900}
        onCancel={() => setDrawerVisible(false)}
      >
        <Tabs items={items} />
      </PageModal>
    );
  };

  // ✅ Enhanced form with comprehensive fields
  const renderForm = () => (
    <div
      className="rounded-xl border mb-5"
      style={{
        backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9fafb',
        borderColor: isDark ? defaultColors.palette.gray[600] : '#e5e7eb',
        borderLeftColor: isEdit ? '#f59e0b' : defaultColors.primary,
        borderLeftWidth: 4,
        borderLeftStyle: 'solid',
        padding: '20px 24px 12px',
      }}
    >
      <div className="flex items-center gap-2 mb-4">
        <div
          className="p-1.5 rounded-md"
          style={{
            backgroundColor: isEdit
              ? isDark
                ? 'rgba(245,158,11,0.08)'
                : '#fffbeb'
              : isDark
              ? defaultColors.palette.teal[900]
              : defaultColors.palette.teal[50],
          }}
        >
          {isEdit ? (
            <Icons.Pencil className="w-4 h-4" style={{ color: '#f59e0b' }} />
          ) : (
            <Icons.Plus className="w-4 h-4" style={{ color: defaultColors.primary }} />
          )}
        </div>
        <span className="text-sm font-semibold" style={{ color: isEdit ? '#f59e0b' : defaultColors.primary }}>
          {isEdit ? 'Edit Cleaning Type' : 'Add New Cleaning Type'}
        </span>
        {isEdit && (
          <Tag color="warning" className="ml-2 text-xs">
            Editing
          </Tag>
        )}
      </div>
      <Formik
        enableReinitialize
        initialValues={
          isEdit && editData
            ? {
                id: editData?.id || null,
                name: editData?.name || '',
                description: editData?.description || '',
                cleaning_code: editData?.cleaning_code || '',
                cleaning_class: editData?.cleaning_class || '',
                campaign_batch_limit: editData?.campaign_batch_limit ?? null,
                campaign_max_days: editData?.campaign_max_days ?? null,
                dirty_hold_hours: editData?.dirty_hold_hours ?? null,
                cleaning_category: editData?.cleaning_category || '',
                cleaning_method: editData?.cleaning_method || '',
                estimated_duration_minutes: editData?.estimated_duration_minutes || null,
                drying_time_minutes: editData?.drying_time_minutes || null,
                hold_time_hours: editData?.hold_time_hours || null,
                frequency_requirement: editData?.frequency_requirement || '',
                cleaning_agents: editData?.cleaning_agents || '',
                detergent_type: editData?.detergent_type || '',
                sanitizer_type: editData?.sanitizer_type || '',
                rinse_agent: editData?.rinse_agent || '',
                water_quality_required: editData?.water_quality_required || '',
                equipment_tools_required: editData?.equipment_tools_required || '',
                ppe_requirements: editData?.ppe_requirements || '',
                minimum_personnel_required: editData?.minimum_personnel_required || 1,
                operator_qualification_required: editData?.operator_qualification_required || '',
                training_required:
                  editData?.training_required !== undefined ? editData.training_required : true,
                training_reference: editData?.training_reference || '',
                verification_method: editData?.verification_method || '',
                acceptance_criteria: editData?.acceptance_criteria || '',
                visual_inspection_required:
                  editData?.visual_inspection_required !== undefined
                    ? editData.visual_inspection_required
                    : true,
                swab_test_required: editData?.swab_test_required || false,
                rinse_test_required: editData?.rinse_test_required || false,
                microbial_test_required: editData?.microbial_test_required || false,
                residue_limit_ppm: editData?.residue_limit_ppm || null,
                sop_reference: editData?.sop_reference || '',
                checklist_required:
                  editData?.checklist_required !== undefined ? editData.checklist_required : true,
                photographic_evidence_required: editData?.photographic_evidence_required || false,
                batch_record_entry_required:
                  editData?.batch_record_entry_required !== undefined
                    ? editData.batch_record_entry_required
                    : true,
                validation_required:
                  editData?.validation_required !== undefined ? editData.validation_required : true,
                validation_protocol_reference: editData?.validation_protocol_reference || '',
                validation_status: editData?.validation_status || 'NOT_VALIDATED',
                cgmp_critical:
                  editData?.cgmp_critical !== undefined ? editData.cgmp_critical : true,
                regulatory_requirement: editData?.regulatory_requirement || '',
                risk_level: editData?.risk_level || '',
                safety_precautions: editData?.safety_precautions || '',
                environmental_controls_required: editData?.environmental_controls_required || false,
                applicable_equipment: editData?.applicable_equipment || '',
                applicable_areas: editData?.applicable_areas || '',
                product_contact_surfaces_only: editData?.product_contact_surfaces_only || false,
                is_active: editData?.is_active !== undefined ? editData.is_active : true,
                requires_supervisor_approval: editData?.requires_supervisor_approval || false,
                requires_qa_approval: editData?.requires_qa_approval || false,
                critical_to_quality: editData?.critical_to_quality || false,
                notes: editData?.notes || '',
              }
            : {
                name: '',
                description: '',
                cleaning_code: '',
                cleaning_class: '',
                campaign_batch_limit: null,
                campaign_max_days: null,
                dirty_hold_hours: null,
                cleaning_category: '',
                cleaning_method: '',
                estimated_duration_minutes: null,
                drying_time_minutes: null,
                hold_time_hours: null,
                frequency_requirement: '',
                cleaning_agents: '',
                detergent_type: '',
                sanitizer_type: '',
                rinse_agent: '',
                water_quality_required: '',
                equipment_tools_required: '',
                ppe_requirements: '',
                minimum_personnel_required: 1,
                operator_qualification_required: '',
                training_required: true,
                training_reference: '',
                verification_method: '',
                acceptance_criteria: '',
                visual_inspection_required: true,
                swab_test_required: false,
                rinse_test_required: false,
                microbial_test_required: false,
                residue_limit_ppm: null,
                sop_reference: '',
                checklist_required: true,
                photographic_evidence_required: false,
                batch_record_entry_required: true,
                validation_required: true,
                validation_protocol_reference: '',
                validation_status: 'NOT_VALIDATED',
                cgmp_critical: true,
                regulatory_requirement: '',
                risk_level: '',
                safety_precautions: '',
                environmental_controls_required: false,
                applicable_equipment: '',
                applicable_areas: '',
                product_contact_surfaces_only: false,
                is_active: true,
                requires_supervisor_approval: false,
                requires_qa_approval: false,
                critical_to_quality: false,
                notes: '',
              }
        }
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => handleAdd(values, resetForm)}
      >
        {({ values, handleChange, setFieldValue, handleSubmit, errors, touched, handleReset }) => (
          <Form layout="vertical" onFinish={handleSubmit}>
            {/* Basic Information */}
            <Divider orientation="left">General Information</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Cleaning Type Name"
                  validateStatus={touched.name && errors.name ? 'error' : ''}
                  help={touched.name && errors.name ? errors.name : ''}
                  required
                >
                  <Input
                    name="name"
                    placeholder="Enter Cleaning Type Name"
                    value={values.name}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Cleaning Code">
                  <Input
                    name="cleaning_code"
                    placeholder="e.g., CLN-001"
                    value={values.cleaning_code}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Class"
                  tooltip="The tier the trigger resolver and the execution gate read. Type A is a same-product campaign continuation; Type B is the full clean a changeover, campaign end, maintenance return or contamination deviation requires."
                >
                  <Select
                    placeholder="Select Class"
                    value={values.cleaning_class}
                    onChange={(val) => setFieldValue('cleaning_class', val)}
                    allowClear
                  >
                    <Option value="TYPE_A">Type A — same-product / campaign continuation</Option>
                    <Option value="TYPE_B">Type B — changeover / campaign end / full clean</Option>
                    <Option value="TYPE_C">Type C — sanitisation only</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Campaign Limit — batches"
                  tooltip="Batches this clean may carry before a full (Type B) clean is due."
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    placeholder="e.g. 3"
                    value={values.campaign_batch_limit}
                    onChange={(val) => setFieldValue('campaign_batch_limit', val)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Campaign Limit — days"
                  tooltip="Elapsed days this clean may carry before a full (Type B) clean is due."
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={0.5}
                    placeholder="e.g. 3"
                    value={values.campaign_max_days}
                    onChange={(val) => setFieldValue('campaign_max_days', val)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Dirty Hold (hours)"
                  tooltip="NMT hours from the end of the last use to the start of cleaning."
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="e.g. 23"
                    value={values.dirty_hold_hours}
                    onChange={(val) => setFieldValue('dirty_hold_hours', val)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Category">
                  <Select
                    placeholder="Select Category"
                    value={values.cleaning_category}
                    onChange={(val) => setFieldValue('cleaning_category', val)}
                    allowClear
                  >
                    <Option value="ROUTINE">Routine Cleaning</Option>
                    <Option value="CHANGEOVER">Product Changeover</Option>
                    <Option value="CAMPAIGN">Campaign Cleaning</Option>
                    <Option value="DEEP">Deep/Terminal Cleaning</Option>
                    <Option value="EMERGENCY">Emergency/Breakdown</Option>
                    <Option value="SANITIZATION">Sanitization</Option>
                    <Option value="STERILIZATION">Sterilization</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Description">
                  <TextArea
                    name="description"
                    placeholder="Enter detailed description"
                    value={values.description}
                    onChange={handleChange}
                    rows={3}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Classification & Timing */}
            <Divider orientation="left">Method & Timing</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Cleaning Method">
                  <Select
                    placeholder="Select Method"
                    value={values.cleaning_method}
                    onChange={(val) => setFieldValue('cleaning_method', val)}
                    allowClear
                  >
                    <Option value="DRY">Dry Cleaning</Option>
                    <Option value="WET">Wet Cleaning</Option>
                    <Option value="DRY_WET">Dry + Wet Cleaning</Option>
                    <Option value="CIP">Clean-In-Place (CIP)</Option>
                    <Option value="MANUAL">Manual Cleaning</Option>
                    <Option value="STEAM">Steam Cleaning</Option>
                    <Option value="AUTOMATED">Automated Cleaning</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Duration (minutes)">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Minutes"
                    value={values.estimated_duration_minutes}
                    onChange={(val) => setFieldValue('estimated_duration_minutes', val)}
                    min={0}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Drying Time (minutes)">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Minutes"
                    value={values.drying_time_minutes}
                    onChange={(val) => setFieldValue('drying_time_minutes', val)}
                    min={0}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Hold Time (hours)">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Hours"
                    value={values.hold_time_hours}
                    onChange={(val) => setFieldValue('hold_time_hours', val)}
                    min={0}
                    step={0.5}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Frequency Requirement">
                  <Input
                    name="frequency_requirement"
                    placeholder="e.g., After each batch, Daily, Weekly"
                    value={values.frequency_requirement}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Verification & Quality */}
            <Divider orientation="left">Verification & Quality</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Verification Method">
                  <Select
                    placeholder="Select Method"
                    value={values.verification_method}
                    onChange={(val) => setFieldValue('verification_method', val)}
                    allowClear
                  >
                    <Option value="VISUAL">Visual Inspection</Option>
                    <Option value="SWAB">Swab Test</Option>
                    <Option value="RINSE">Rinse Test</Option>
                    <Option value="TOC">TOC Analysis</Option>
                    <Option value="MICROBIAL">Microbial Test</Option>
                    <Option value="COMBINATION">Combination of Methods</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Water Quality Required">
                  <Select
                    placeholder="Select Water Quality"
                    value={values.water_quality_required}
                    onChange={(val) => setFieldValue('water_quality_required', val)}
                    allowClear
                  >
                    <Option value="POTABLE">Potable Water</Option>
                    <Option value="PURIFIED">Purified Water</Option>
                    <Option value="WFI">Water For Injection (WFI)</Option>
                    <Option value="DEIONIZED">Deionized Water</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Acceptance Criteria">
                  <Input
                    name="acceptance_criteria"
                    placeholder="e.g., No visible residue, <10 ppm limit"
                    value={values.acceptance_criteria}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Risk & Compliance */}
            <Divider orientation="left">Risk & Compliance</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Risk Level">
                  <Select
                    placeholder="Select Risk Level"
                    value={values.risk_level}
                    onChange={(val) => setFieldValue('risk_level', val)}
                    allowClear
                  >
                    <Option value="LOW">Low Risk</Option>
                    <Option value="MEDIUM">Medium Risk</Option>
                    <Option value="HIGH">High Risk</Option>
                    <Option value="CRITICAL">Critical Risk</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Validation Status">
                  <Select
                    placeholder="Select Status"
                    value={values.validation_status}
                    onChange={(val) => setFieldValue('validation_status', val)}
                  >
                    <Option value="NOT_VALIDATED">Not Validated</Option>
                    <Option value="IN_PROGRESS">Validation In Progress</Option>
                    <Option value="VALIDATED">Validated</Option>
                    <Option value="REVALIDATION_DUE">Revalidation Due</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="SOP Reference">
                  <Input
                    name="sop_reference"
                    placeholder="e.g., SOP-CLN-001"
                    value={values.sop_reference}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Status">
                  <Select
                    value={values.is_active}
                    onChange={(val) => setFieldValue('is_active', val)}
                  >
                    <Option value={true}>Active</Option>
                    <Option value={false}>Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Checkboxes */}
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item>
                  <Checkbox
                    checked={values.cgmp_critical}
                    onChange={(e) => setFieldValue('cgmp_critical', e.target.checked)}
                  >
                    cGMP Critical
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Checkbox
                    checked={values.requires_supervisor_approval}
                    onChange={(e) =>
                      setFieldValue('requires_supervisor_approval', e.target.checked)
                    }
                  >
                    Supervisor Approval Required
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Checkbox
                    checked={values.requires_qa_approval}
                    onChange={(e) => setFieldValue('requires_qa_approval', e.target.checked)}
                  >
                    QA Approval Required
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Checkbox
                    checked={values.checklist_required}
                    onChange={(e) => setFieldValue('checklist_required', e.target.checked)}
                  >
                    Checklist Required (BMR)
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>

            {/* Submit Buttons */}
            <Row>
              <Col span={24} className="flex items-end justify-end gap-2">
                <Button
                  onClick={() => {
                    handleReset();
                    setShowForm(false);
                    setIsEdit(false);
                    setEditData(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={
                    isEdit ? (
                      <Icons.Check className="w-4 h-4" />
                    ) : (
                      <Icons.Plus className="w-4 h-4" />
                    )
                  }
                  style={isEdit ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b' } : {}}
                >
                  {isEdit ? 'Update' : 'Add'}
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        classNames={{ body: 'page-card-scroll' }}
        style={{
          backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff',
          height: 'calc(100dvh - 120px)',
          display: 'flex',
          flexDirection: 'column',
        }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
        title={
          <PageHeader
            compact
            icon={<Icons.Sparkles size={22} />}
            title={t('Cleaning Types Configuration')}
            description="Cleaning procedures (type, method, duration, validation) available for line and equipment cleaning."
            meta={!CLEANING_EDIT_ENABLED ? <PageInfo text="Editing cleaning types is disabled on this server. Existing cleaning types can be viewed and new ones added, but edit and delete were turned off on the backend (2026-07-09)." /> : undefined}
            extra={
              showForm ? (
                <Button
                  icon={<Icons.X className="w-4 h-4" />}
                  onClick={() => {
                    setShowForm(false);
                    setIsEdit(false);
                    setEditData(null);
                  }}
                >
                  Close form
                </Button>
              ) : (
                <Button
                  type="primary"
                  icon={<Icons.Plus className="w-4 h-4" />}
                  onClick={() => {
                    setShowForm(true);
                    setIsEdit(false);
                    setEditData(null);
                  }}
                >
                  Add Cleaning Type
                </Button>
              )
            }
          />
        }
      >
        {/* Top Summary Cards */}
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              label="Total Cleaning Types"
              value={CleaningData?.length || 0}
              icon={<Sparkles size={24} />}
              bgColor={`${defaultColors.primary}22`}
              iconColor={defaultColors.primary}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              label="Validated Types"
              value={CleaningData?.filter((c) => c?.validation_status === 'VALIDATED')?.length || 0}
              icon={<CheckCircle size={24} />}
              bgColor={`${defaultColors.success}22`}
              iconColor={defaultColors.success}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              label="High Risk"
              value={
                CleaningData?.filter(
                  (c) => c?.risk_level === 'HIGH' || c?.risk_level === 'CRITICAL'
                )?.length || 0
              }
              icon={<AlertTriangle size={24} />}
              bgColor={`${defaultColors.error}22`}
              iconColor={defaultColors.error}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              label="Revalidation Due"
              value={
                CleaningData?.filter((c) => c?.validation_status === 'REVALIDATION_DUE')?.length ||
                0
              }
              icon={<Clock size={24} />}
              bgColor={`${defaultColors.warning}22`}
              iconColor={defaultColors.warning}
            />
          </Col>
        </Row>

        {showForm && renderForm()}

        <DataTable
          tableKey="cleaningTypesTable"
          searchable
          searchPlaceholder="Search cleaning types..."
          columnConfig
          showCount={false}
          bordered
          data={CleaningData}
          columns={allColumns}
          rowKey="id"
          exportable
          exportFilename="cleaning_types"
          scrollX="max-content"
          height={null}
          locale={{
            emptyText: (
              <EmptyState
                title="No cleaning types yet"
                description="Add a cleaning type to make it available for line and equipment cleaning."
                actionLabel="Add Cleaning Type"
                onAction={() => {
                  setShowForm(true);
                  setIsEdit(false);
                  setEditData(null);
                }}
              />
            ),
          }}
        />

        {renderDetailDrawer()}
      </Card>
    </div>
  );
};

export default CleaningTypes;
